import { useEffect, useState } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";

import {
  Table as MtTable,
  Button,
  Text,
  ScrollArea,
  Flex,
  useMantineTheme,
  Skeleton,
} from "@mantine/core";
import { PaginationState, TableProps } from "types/ui";

const Table: React.FC<TableProps<any>> = ({
  isLoading = false,
  data = [],
  columns = [],
  tfoot = null,
  pageSize = 10,
  externalPagination = false,
  currentPage = 1,
  totalPages,
  canPreviousPage = true,
  canNextPage = true,
  onPageChange,
  hideFirstLast = false,
}) => {
  const theme = useMantineTheme();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
  });

  useEffect(() => {
    if (
      table.getState().pagination.pageIndex + 1 >
      table.getPageCount()
    ) {
      table.setPageIndex(table.getPageCount() - 1);
    }
  }, [data]);

  return (
    <Flex direction="column">
      <ScrollArea>
        <MtTable highlightOnHover>
          <MtTable.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <MtTable.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <MtTable.Th
                    key={header.id}
                    onClick={
                      columns[index]?.enableSorting === false ? () => { }
                        : header.column.getToggleSortingHandler()
                    }
                    bg={theme.colors.gray[1]}
                    style={{
                      width: `${header.getSize() !== 150
                        ? header.getSize() + "px"
                        : "auto"
                        }`,
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <Flex align="center" gap="xs" mt={0}>
                        <Text size="xs" fw="700">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Text>
                        {
                          (columns[index]?.enableSorting === false) || (
                            <>
                              {!header.column.getIsSorted() && <FaSort />}
                              {
                                { asc: <FaSortUp />, desc: <FaSortDown /> }[
                                header.column.getIsSorted() as "asc" | "desc" | undefined
                                ]
                              }
                            </>
                          )}
                      </Flex>
                    )}
                  </MtTable.Th>
                ))}
              </MtTable.Tr>
            ))}
          </MtTable.Thead>
          <MtTable.Tbody>
            {isLoading &&
              Array.from({ length: pageSize }).map((_, rowIndex) => (
                <MtTable.Tr key={`skeleton-row-${rowIndex}`}>
                  {columns.map((_, colIndex) => (
                    <MtTable.Td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                      <Skeleton height={20} radius="sm" />
                    </MtTable.Td>
                  ))}
                </MtTable.Tr>
              ))}
            {!isLoading &&
              table.getRowModel().rows.map((row) => (
                <MtTable.Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <MtTable.Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </MtTable.Td>
                  ))}
                </MtTable.Tr>
              ))}
          </MtTable.Tbody>
          {tfoot && <MtTable.Tfoot>{tfoot}</MtTable.Tfoot>}
        </MtTable>
      </ScrollArea>
      {
        (externalPagination || table.getPageCount() > 0) && (
          <Flex justify="space-between">
            <Text fw={700}>
              Page {externalPagination ? currentPage : table.getState().pagination.pageIndex + 1} of{" "}
              {externalPagination ? (totalPages || "?") : table.getPageCount().toLocaleString()}
            </Text>
            <Flex gap="xs" mt={0}>
              {!hideFirstLast && (
                <Button
                  size="xs"
                  onClick={() => {
                    externalPagination ? onPageChange?.(1) : table.setPageIndex(0);
                  }}
                  disabled={externalPagination ? !canPreviousPage : !table.getCanPreviousPage()}
                  variant="default"
                >
                  First
                </Button>
              )}
              <Button
                size="xs"
                onClick={() => {
                  externalPagination ? onPageChange?.(currentPage - 1) : table.previousPage();
                }}
                disabled={externalPagination ? !canPreviousPage : !table.getCanPreviousPage()}
                variant="default"
              >
                Prev
              </Button>
              <Button
                size="xs"
                onClick={() => {
                  externalPagination ? onPageChange?.(currentPage + 1) : table.nextPage();
                }}
                disabled={externalPagination ? !canNextPage : !table.getCanNextPage()}
                variant="default"
              >
                Next
              </Button>
              {!hideFirstLast && (
                <Button
                  size="xs"
                  onClick={() => {
                    externalPagination ? onPageChange?.(totalPages || 1) : table.setPageIndex(table.getPageCount() - 1);
                  }}
                  disabled={externalPagination ? !canNextPage : !table.getCanNextPage()}
                  variant="default"
                >
                  Last
                </Button>
              )}
            </Flex>
          </Flex>
        )
      }
    </Flex >
  );
};

export default Table;
